import { useSnackbar } from 'notistack';
import { FormEvent, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';
import * as yup from 'yup';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AppDispatch } from '~/store';

import {
  EmpOffContactType,
  Office,
  getCompanyOffices,
  putEmpOffContactData,
} from './data';

export type IFormInput = {
  fEmpEmailWork: string;
  fEmpOfficeFloor?: string;
  fEmpOfficeLoc: number;
  fEmpOfficeRoom?: string;
  fEmpPhoneMob?: string;
  fEmpPhoneWork?: string;
};

export type useWorkContractOutput = {
  readMode: boolean;
  setReadMode: (data: boolean) => void;
  empData: EmpOffContactType;
  onSubmit: (e: FormEvent) => void;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  control: Control<IFormInput>;
  companyOffice: Office[];
  handleFormReset: () => void;
  isLoading: boolean;
};

export const useGetCompanyOffices = () => {
  const { data, ...rest } = useSWR(['companyOffices'], () =>
    getCompanyOffices(),
  );

  return {
    data: data ?? [],
    ...rest,
  };
};

export const useWorkContract = (): useWorkContractOutput => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const {
    employee: { empWorkContact: empData },
    isLoading: isLoadingEmployee,
  } = useEmployee();

  const { data: companyOffice, isLoading: isLoadingCompanyOffices } =
    useGetCompanyOffices();

  const isLoading = isLoadingEmployee || isLoadingCompanyOffices;

  const [readMode, setReadMode] = useState(true);
  const userProfile = useLoggedInEmployeeProfile();

  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fEmpEmailWork: yup.string().required(t('laFillManFields')),
      fEmpOfficeFloor: yup.string(),
      fEmpOfficeLoc: yup.number().required(t('laFillManFields')),
      fEmpOfficeRoom: yup.string(),
      fEmpPhoneMob: yup.string(),
      fEmpPhoneWork: yup.string(),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm<IFormInput>({
    resolver,
  });

  const handleFormReset = () => {
    reset();
    setReadMode(true);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpEmailWork: data.fEmpEmailWork,
        fEmpOfficeFloor: data.fEmpOfficeFloor,
        fEmpOfficeLoc: data.fEmpOfficeLoc,
        fEmpOfficeRoom: data.fEmpOfficeRoom,
        fEmpPhoneMob: data.fEmpPhoneMob,
        fEmpPhoneWork: data.fEmpPhoneWork,
      };
      (async () => {
        try {
          await putEmpOffContactData(json, userProfile.id);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: userProfile.id }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, enqueueSnackbar, handleSubmit, t, userProfile.id]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  return {
    control,
    companyOffice,
    readMode,
    setReadMode,
    empData,
    errors,
    register,
    onSubmit,
    handleFormReset,
    isLoading,
  };
};
